import { CustomTab } from "@/components/UI/Tabs";
import { formatDate, getStatusColor } from "@/utils/helper";
import {
  Button,
  Col,
  Modal,
  Row,
  Typography,
  Upload,
  Form,
  message,
  InputNumber,
  Tag,
} from "antd";
import { useState, useMemo } from "react";
import {
  //   ClockCircleOutlined,
  InboxOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";
import {
  useSendEventQuoteMailMutation,
  useUpdateEventQuoteMutation,
} from "@/services/api/cateringevent/cateringEventApi";

const { Title } = Typography;
const { Dragger } = Upload;

const BookedEventDetailsModal = ({ open, onClose, orderData }) => {
  const [updateEventQuote, { isLoading: updatingQuote }] =
    useUpdateEventQuoteMutation();
  const [sendEventQuoteMail] = useSendEventQuoteMailMutation();
  //   console.log("Modal orderData:", orderData);
  //   console.log("Modal originalData:", orderData?.originalData);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const eventId = orderData?.originalData.id;
  const tabs = [{ label: "Order Summary" }, { label: "Send Quote" }];

  const checkFormValidity = () => {
    const quoteAmount = form.getFieldValue("quoteAmount");
    const quoteUrl = form.getFieldValue("quoteUrl");

    setIsFormValid(!!quoteAmount && !!quoteUrl);
  };

  // Map API data to catering details with fallback values
  const cateringDetails = useMemo(() => {
    // Check for event_details in the API response
    const eventData = orderData?.originalData || orderData || {};
    const deliveryMode = orderData?.delivery_mode || "Delivery";

    return [
      {
        question: "When is the event?",
        answer: eventData.event_date
          ? formatDate(eventData.event_date)
          : orderData?.start_date
          ? formatDate(orderData.start_date)
          : "Date not specified",
      },
      {
        question: "What type of event are you planning?",
        answer: eventData.event_type || "Not specified",
      },
      {
        question: "How many guests are you expecting?",
        answer: eventData.expected_guests?.toString() || "Not specified",
      },
      {
        question: "How would you want us to provide the catering service?",
        answer: eventData.service_type || deliveryMode || "Not specified",
      },
      {
        question: "Venue/ Delivery Location",
        answer:
          eventData.venue ||
          eventData.pickup_address ||
          "Location not specified",
      },
      {
        question: "Dietary Requirements",
        answer: eventData.dietary_preferences || "None specified",
      },
      {
        question: "Drinks",
        answer: eventData.beverage_types || "Not specified",
      },
      {
        question: "Package type",
        answer: eventData.package_types || "Not specified",
      },
      {
        question: "Customer's Role",
        answer: eventData.role || "Not specified",
      },
      {
        question: "Customer's Contact",
        answer: eventData.phone_number || "Not specified",
      },
      {
        question: "Budget (per person)",
        answer: eventData.budget_per_person
          ? `₦${parseFloat(eventData.budget_per_person).toLocaleString()}`
          : "Not specified",
      },
      {
        question: "Total Budget",
        answer: eventData.budget_per_person
          ? `₦${parseFloat(eventData.total_budget).toLocaleString()}`
          : "Not specified",
      },
      {
        question: "Food Request",
        answer: eventData.food_request || "Not Specified",
      },
    ];
  }, [orderData]);

  const handlePdfUpload = async (file) => {
    try {
      setUploading(true);
      message.loading({ content: "Uploading PDF...", key: "pdfUpload" });

      const url = await uploadToCloudinary(file);

      setPdfUrl(url);
      form.setFieldValue("quoteUrl", url);

      message.success({
        content: "PDF uploaded successfully!",
        key: "pdfUpload",
      });

      return url;
    } catch (error) {
      console.error("PDF upload failed:", error);
      message.error({
        content: "Failed to upload PDF. Please try again.",
        key: "pdfUpload",
      });
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // PDF Upload configuration
  const uploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: ".pdf",
    fileList: fileList,
    beforeUpload: async (file) => {
      const isPDF = file.type === "application/pdf";
      if (!isPDF) {
        message.error("You can only upload PDF files!");
        return Upload.LIST_IGNORE;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("PDF must be smaller than 10MB!");
        return Upload.LIST_IGNORE;
      }

      // Upload to Cloudinary
      try {
        await handlePdfUpload(file);
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: "done",
            url: pdfUrl,
          },
        ]);
      } catch (error) {
        return Upload.LIST_IGNORE;
      }

      // Prevent default upload behavior
      return false;
    },
    onRemove: () => {
      setFileList([]);
      setPdfUrl(null);
      form.setFieldValue("quoteUrl", null);
    },
  };

  // Preview PDF in new tab
  const handlePreview = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  const handleSaveAndSendQuote = async () => {
    try {
      await form.validateFields();

      const orderPayload = {
        quote_url: form.getFieldValue("quoteUrl"),
        quote_amount: form.getFieldValue("quoteAmount"),
      };

      // Save quote first
      await updateEventQuote({
        id: eventId,
        requestData: orderPayload,
      }).unwrap();

      // Then send email on success
      await sendEventQuoteMail({
        id: eventId,
      }).unwrap();

      message.success("Quote saved and sent to customer successfully!");
      form.resetFields();
      setFileList([]);
      setPdfUrl(null);
      onClose();
    } catch (error) {
      console.error("Error saving and sending quote:", error);
      message.error(
        error?.data?.message ||
          "Failed to save and send quote. Please try again."
      );
    }
  };
  const renderTabContent = () => {
    if (activeTab === 0) {
      return (
        <div className="mt-6">
          <Row gutter={[24, 24]}>
            {cateringDetails.map((detail, index) => (
              <Col xs={24} md={12} key={index}>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {detail.question}
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-600">
                    {Array.isArray(detail.answer) ? (
                      <div className="flex flex-wrap gap-2">
                        {detail.answer.map((item, i) => (
                          <Tag key={i} color="orange">
                            {item}
                          </Tag>
                        ))}
                      </div>
                    ) : (
                      detail.answer
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      );
    }

    return (
      <div className="mt-6">
        <Form form={form} layout="vertical" onValuesChange={checkFormValidity}>
          <Form.Item
            label="Quote Amount"
            name="quoteAmount"
            rules={[
              { required: true, message: "Please enter the quote amount" },
            ]}
          >
            <InputNumber
              placeholder="500000"
              className="w-full"
              size="large"
              min={0}
              formatter={(value) =>
                `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/₦\s?|(,*)/g, "")}
            />
          </Form.Item>
          <Form.Item
            label="Upload Quote (PDF)"
            name="quoteUrl"
            rules={[{ required: true, message: "Please upload a quote PDF" }]}
          >
            <div className="space-y-3">
              <Dragger
                {...uploadProps}
                className="hover:border-orange-500"
                disabled={uploading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined
                    className="text-orange-500"
                    style={{ fontSize: 48 }}
                  />
                </p>
                <p className=" ">
                  {uploading
                    ? "Uploading PDF..."
                    : "Click or drag PDF file to this area to upload"}
                </p>
                <p className=" text-sm text-gray-500">
                  Support for a single PDF upload. Maximum file size: 10MB
                </p>
              </Dragger>

              {/* PDF Preview Section */}
              {pdfUrl && fileList.length > 0 && (
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {fileList[0]?.name || "Quote PDF"}
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF uploaded successfully
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="default"
                      icon={<EyeOutlined />}
                      onClick={handlePreview}
                      size="small"
                    >
                      Preview
                    </Button>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setFileList([]);
                        setPdfUrl(null);
                        form.setFieldValue("quoteUrl", null);
                      }}
                      size="small"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Form.Item>
        </Form>
      </div>
    );
  };

  const { color, backgroundColor } = getStatusColor(
    orderData?.status?.name || orderData?.status || "Incoming Request"
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="space-y-1">
          <Title level={5} className="mb-2">
            Booked Event Details{" "}
            <span
              className="px-3 py-1 rounded-lg text-sm ml-2"
              style={{ color, backgroundColor }}
            >
              {orderData?.status?.name ||
                orderData?.status ||
                "Incoming Request"}
            </span>
          </Title>
          <p className="text-sm text-gray-400 m-0">
            See details of customer's event booking
          </p>
        </div>
      }
      maskClosable
      destroyOnClose
      width="90vw"
      style={{ top: 10, maxWidth: 1400 }}
      styles={{ body: { maxHeight: "calc(100vh - 190px)", overflowY: "auto" } }}
      footer={
        <div className="flex justify-between items-center">
          <Button onClick={onClose} size="medium">
            Cancel
          </Button>
          <div className="flex gap-3">
            {/* <Button
              onClick={handleSendQuoteMail}
              type="primary"
              size="medium"
              disabled={!isFormValid || activeTab === 0}
              loading={sendingQuote}
              className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:from-[#F9A022] hover:to-[#D06D01] border-0"
            >
              Send quote email
            </Button> */}
            <Button
              type="primary"
              size="medium"
              onClick={handleSaveAndSendQuote}
              disabled={!isFormValid || activeTab === 0}
              loading={updatingQuote}
              className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:from-[#F9A022] hover:to-[#D06D01] border-0"
            >
              Save & Send quote
            </Button>
          </div>
        </div>
      }
    >
      <div className="min-h-[70vh] p-6">
        <Row gutter={[24, 24]} className="my-4">
          <Col xs={24} lg={5}>
            <div className="mt-6 space-y-6 gap-6 flex lg:block">
              {[
                {
                  label: "CUSTOMER",
                  value: orderData?.originalData?.customer_name,
                },
                {
                  label: "ORDER PLACED",
                  value: orderData?.originalData?.created_at || "N/A",
                },
                {
                  label: "DUE BY",
                  value: orderData?.originalData?.pickup_date || "N/A",
                },
              ].map((item, i) => (
                <div key={i}>
                  <div className="text-xs text-gray-500 mb-2">{item.label}</div>
                  {item.isEstimate ? (
                    <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                      {item.icon}
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ) : (
                    <div className={item.icon ? "flex items-start gap-2" : ""}>
                      {item.icon}
                      <div>
                        <div className="font-medium">{item.value}</div>
                        {item.extra?.length > 0 &&
                          item.extra.map((text, j) => (
                            <div key={j} className="text-sm text-gray-500 mt-1">
                              {text}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Col>

          <Col
            xs={24}
            lg={19}
            className="p-8 border border-gray-200 rounded-lg bg-white"
          >
            <CustomTab
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            {renderTabContent()}
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default BookedEventDetailsModal;
